import React from 'react';
import PropTypes from 'prop-types';

import { Button, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import { bandTier } from 'components/upb_match_explorer/constants';
import {
  BG_1,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_COLOR,
  RADIUS_LG,
  SHADOW_SM,
  WARNING_LIGHT,
  WARNING_TEXT,
} from 'components/upb_viewer_beta/css/betaTokens';
import { fmtInt, fmtPct, fmtUsd } from 'components/upb_match_explorer/format';
import InfluenceInfoTip from 'components/influence_sku_mapping/InfluenceInfoTip';

const TAM_TIP =
  'TAM: influence-enabled distributor SKUs on the manufacturer’s campaign brands that are either ' +
  'mapped to a campaign MPN or not yet mapped to any MPN (available to map).';
const MAPPED_TIP =
  'Mapped SKUs ÷ TAM SKUs. Mapped = branded SKUs linked to the manufacturer’s catalog.';
const GMV_TIP = 'Annualized GMV of mapped SKUs ÷ annualized GMV of TAM SKUs.';
const GMV_DOLLAR_TIP = 'Total annualized GMV of this manufacturer’s TAM SKUs.';
const DP_TIP = 'Distinct influence-enabled distributors carrying this manufacturer’s TAM SKUs.';
const CAMPAIGN_TIP =
  'Campaign MPNs ÷ active MPNs. Manufacturer catalog products (MPNs) enrolled in an active or ' +
  'draft Influence campaign, out of those active on C+D.';

/**
 * InfluenceManufacturerTable — influence-enabled manufacturers with their
 * mapped-of-TAM SKU counts, TAM-based mapping rate, GMV rate, and a
 * drill-in to the per-distributor view.
 */
function InfluenceManufacturerTable({ rows, onAnalyze }) {
  if (!rows.length) {
    return <div className={css(styles.empty)}>No manufacturers match the current filters.</div>;
  }

  return (
    <div className={css(styles.tableWrap)}>
      <Table hover className={css(styles.table)}>
        <thead>
          <tr>
            <th>Manufacturer</th>
            <th className={css(styles.numCol)}>
              Campaign / Active
              <InfluenceInfoTip id="tip-campaign" text={CAMPAIGN_TIP} />
            </th>
            <th className={css(styles.numCol)}>
              Mapped / TAM
              <InfluenceInfoTip id="tip-tam" text={TAM_TIP} />
            </th>
            <th className={css(styles.numCol)}>
              Mapped %<InfluenceInfoTip id="tip-mapped" text={MAPPED_TIP} />
            </th>
            <th className={css(styles.numCol)}>
              GMV %<InfluenceInfoTip id="tip-gmv" text={GMV_TIP} />
            </th>
            <th className={css(styles.numCol)}>
              GMV
              <InfluenceInfoTip id="tip-gmv-dollar" text={GMV_DOLLAR_TIP} />
            </th>
            <th className={css(styles.numCol)}>
              Influence DPs
              <InfluenceInfoTip id="tip-dps" text={DP_TIP} />
            </th>
            <th className={css(styles.actionCol)} />
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const tier = bandTier(row.band);
            return (
              <tr key={row.manufacturer_id}>
                <td>
                  <div className={css(styles.mfrCell)}>
                    {row.logo_url ? (
                      <img
                        className={css(styles.logo)}
                        src={row.logo_url}
                        alt=""
                        onError={e => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className={css(styles.logoFallback)}>
                        {(row.name || '?').charAt(0)}
                      </span>
                    )}
                    <div>
                      <div className={css(styles.mfrName)}>
                        {row.name}
                        {row.influence_status === 'DRAFT' && (
                          <span className={css(styles.onboardingPill)}>onboarding</span>
                        )}
                      </div>
                      <div className={css(styles.mfrSub)}>
                        {fmtInt(row.total)} branded dist. SKUs
                      </div>
                    </div>
                  </div>
                </td>
                <td className={css(styles.numCol)}>
                  <span className={css(styles.mappedFrac)}>{fmtInt(row.campaign_skus)}</span>
                  <span className={css(styles.tamDenom)}> / {fmtInt(row.active_catalog_skus)}</span>
                </td>
                <td className={css(styles.numCol)}>
                  <span className={css(styles.mappedFrac)}>{fmtInt(row.mapped)}</span>
                  <span className={css(styles.tamDenom)}> / {fmtInt(row.tam)}</span>
                </td>
                <td className={css(styles.numCol)}>{fmtPct(row.mapped_pct)}</td>
                <td className={css(styles.numCol)}>
                  <span
                    className={css(styles.pctPill)}
                    style={{ backgroundColor: tier.bg, color: tier.text }}
                  >
                    {fmtPct(row.gmv_pct)}
                  </span>
                </td>
                <td className={css(styles.numCol)}>{fmtUsd(row.tam_gmv)}</td>
                <td className={css(styles.numCol)}>{fmtInt(row.dp_count)}</td>
                <td className={css(styles.actionCol)}>
                  <Button variant="link" size="sm" onClick={() => onAnalyze(row.manufacturer_id)}>
                    Analyze →
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

const styles = StyleSheet.create({
  empty: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    padding: '32px 8px',
    color: FG_3,
    fontSize: 14,
    textAlign: 'center',
  },
  tableWrap: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    overflowX: 'auto',
  },
  table: { fontSize: 13, color: FG_2, marginBottom: 0 },
  numCol: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  actionCol: { textAlign: 'right', whiteSpace: 'nowrap' },
  mfrCell: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { width: 32, height: 32, borderRadius: 6, objectFit: 'contain', background: BG_1 },
  logoFallback: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 6,
    background: BORDER_2,
    color: FG_2,
    fontWeight: 700,
    fontSize: 14,
  },
  mfrName: { fontWeight: 600, color: FG_1 },
  onboardingPill: {
    display: 'inline-block',
    marginLeft: 8,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: WARNING_TEXT,
    backgroundColor: WARNING_LIGHT,
    padding: '2px 7px',
    borderRadius: 20,
    verticalAlign: 'middle',
  },
  mfrSub: { fontSize: 12, color: FG_3 },
  mappedFrac: { fontWeight: 600, color: FG_1 },
  tamDenom: { color: FG_3 },
  pctPill: {
    display: 'inline-block',
    minWidth: 58,
    textAlign: 'center',
    fontWeight: 600,
    fontSize: 12.5,
    padding: '3px 8px',
    borderRadius: 20,
  },
});

InfluenceManufacturerTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      manufacturer_id: PropTypes.number,
      name: PropTypes.string,
      logo_url: PropTypes.string,
      influence_status: PropTypes.string,
      total: PropTypes.number,
      active: PropTypes.number,
      mapped: PropTypes.number,
      tam: PropTypes.number,
      campaign_skus: PropTypes.number,
      active_catalog_skus: PropTypes.number,
      dp_count: PropTypes.number,
      mapped_pct: PropTypes.number,
      gmv_pct: PropTypes.number,
      tam_gmv: PropTypes.number,
      band: PropTypes.number,
    }),
  ).isRequired,
  onAnalyze: PropTypes.func.isRequired,
};

export default InfluenceManufacturerTable;
